import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Header from "../component/Header";
import Sidebar from '../component/Sidebar';
import '../css/sidebar.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader2 from "../component/Loader2";
import 'rsuite/dist/rsuite.min.css';
import {
    Lock,
    Eye,
    EyeOff,
    Check,
    X,
    Shield,
    AlertCircle
} from 'lucide-react';

function ChangePassword({setProgress}) {
    const pmsApiBaseUrl =
        process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Password strength checker
    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 20;
        if (password.length >= 12) strength += 20;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
        if (/\d/.test(password)) strength += 20;
        if (/[^A-Za-z0-9]/.test(password)) strength += 20;
        return strength;
    };

    useEffect(() => {
        if (formData.newPassword) {
            setPasswordStrength(checkPasswordStrength(formData.newPassword));
        } else {
            setPasswordStrength(0);
        }
    }, [formData.newPassword]);

    useEffect(() => {
        document.documentElement.classList.add("change-password-route");
        return () => {
            document.documentElement.classList.remove("change-password-route");
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const showToast = (type, text) => {
        const message = String(text || '').trim() || 'Something went wrong';
        if (type === 'success') {
            toast.success(message);
            return;
        }
        if (type === 'info') {
            toast.info(message);
            return;
        }
        toast.error(message);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const parsePmsResponse = async (response) => {
        try {
            const json = await response.json();
            return json?.data || {
                status: "failed",
                message: "Invalid response from server",
            };
        } catch (error) {
            console.error("PMS API parsing error:", error);
            return {
                status: "failed",
                message: "Invalid response from server",
            };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('error', 'Please fix the errors below');
            return;
        }

        setIsSubmitting(true);

        try {
            const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
            const userId =
                Number(localStorage.getItem("id")) ||
                Number(sharedData?.ID) ||
                0;

            if (!userId) {
                showToast('error', 'Session expired. Please login again.');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
                return;
            }

            const payload = {
                user_id: userId,
                old_password: formData.currentPassword,
                new_password: formData.newPassword,
                confirm_password: formData.confirmPassword,
            };

            const response = await fetch(`${pmsApiBaseUrl}/api/v1/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await parsePmsResponse(response);
            const isSuccess = String(data?.status || '').toLowerCase() === 'success';
            const errorMessage =
                data?.message ||
                (Array.isArray(data?.errors) && data.errors[0]) ||
                'Failed to change password';

           if (isSuccess) {
            // Show success toast with longer duration
            showToast('success', data.message || '✅ Password changed successfully! Logging you out...');
            
            // Clear form
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            setIsLoading(true)
            
            // Clear localStorage and redirect after 3 seconds
            setTimeout(() => {
                showToast('info', '🔐 Please login again with your new password');

                setTimeout(() => {
                    localStorage.removeItem('sharedData');
                    setIsLoading(false);
                    navigate('/');
                }, 1000);
            }, 2000);
        }
        else {
                showToast('error', errorMessage);

                if (/old password/i.test(errorMessage)) {
                    setErrors((prev) => ({
                        ...prev,
                        currentPassword: errorMessage,
                    }));
                }
            }
        } catch (error) {
            console.error('Change password error:', error);
            showToast('error', 'An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStrengthColor = () => {
        if (passwordStrength <= 40) return '#ef4444';
        if (passwordStrength <= 80) return '#f59e0b';
        return '#10b981';
    };

    const getStrengthText = () => {
        if (passwordStrength === 0) return '';
        if (passwordStrength <= 40) return 'Weak';
        if (passwordStrength <= 80) return 'Medium';
        return 'Strong';
    };

    return (
        <>
            <Header />
            <div className="wrapper__page change-password-page">
                <Sidebar />


                {isLoading && (
                    <Loader2/>
                )}

                <div className="content_col">
                    <div className="header_text change-password-page__header">
                        <h1>Change Password</h1>
                        <p>Ensure your account is using a strong password to stay secure</p>
                    </div>
                    <div className="change-password-page__body">
                        <div className="change_password_wrapper">

                        {/* Form Card and Security Tips Row */}
                        <div className="password_content_row">
                            {/* Form Card */}
                            <div className="change_password_card">
                                <form onSubmit={handleSubmit}>
                                    {/* Current Password */}
                                    <div className="password_field_group">
                                        <label className="password_label">Current Password</label>
                                        <div className="password_input_wrapper"> 
                                            <div className="password_icon_left">
                                                <Lock size={16} />
                                            </div>
                                            <input
                                                type={showPasswords.current ? 'text' : 'password'}
                                                name="currentPassword"
                                                value={formData.currentPassword}
                                                onChange={handleInputChange}
                                                className={`password_input ${errors.currentPassword ? 'error' : ''}`}
                                                placeholder="Enter your current password"
                                                disabled={isSubmitting}
                                            />
                                            <div
                                                className="password_icon_right"
                                                onClick={() => togglePasswordVisibility('current')}
                                            >
                                                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </div>
                                        </div>
                                        {errors.currentPassword && (
                                            <div className="password_error">{errors.currentPassword}</div>
                                        )}
                                    </div>

                                    {/* New Password */}
                                    <div className="password_field_group">
                                        <label className="password_label">New Password</label>
                                        <div className="password_input_wrapper">
                                            <div className="password_icon_left">
                                                <Lock size={16} />
                                            </div>
                                            <input
                                                type={showPasswords.new ? 'text' : 'password'}
                                                name="newPassword"
                                                value={formData.newPassword}
                                                onChange={handleInputChange}
                                                className={`password_input ${errors.newPassword ? 'error' : ''}`}
                                                placeholder="Enter your new password"
                                                disabled={isSubmitting}
                                            />
                                            <div
                                                className="password_icon_right"
                                                onClick={() => togglePasswordVisibility('new')}
                                            >
                                                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </div>
                                        </div>
                                        {errors.newPassword && (
                                            <div className="password_error">{errors.newPassword}</div>
                                        )}

                                        {/* Password Strength Indicator */}
                                        {formData.newPassword && (
                                            <div className="password_strength">
                                                <div className="strength_progress_container">
                                                    <div className="strength_progress_bar">
                                                        {[20, 40, 60, 80, 100].map((step) => (
                                                            <div
                                                                key={step}
                                                                className={`strength_step ${passwordStrength >= step ? 'active' : ''}`}
                                                                style={{
                                                                    '--step-color': passwordStrength >= step ? getStrengthColor() : '#e5e7eb'
                                                                }}
                                                            ></div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <span className="strength_text" style={{ color: getStrengthColor() }}>
                                                    {getStrengthText()}
                                                </span>
                                            </div>
                                        )}

                                        <div className="password_requirements password_requirements--compact">
                                            <div className="requirement_title">Password must contain:</div>
                                            <ul className="requirement_list">
                                                <li className={`requirement_item ${formData.newPassword.length >= 8 ? 'valid' : formData.newPassword ? 'invalid' : ''}`}>
                                                    8+ characters
                                                </li>
                                                <li className={`requirement_item ${/[A-Z]/.test(formData.newPassword) && /[a-z]/.test(formData.newPassword) ? 'valid' : formData.newPassword ? 'invalid' : ''}`}>
                                                    Upper & lower case
                                                </li>
                                                <li className={`requirement_item ${/\d/.test(formData.newPassword) ? 'valid' : formData.newPassword ? 'invalid' : ''}`}>
                                                    One number
                                                </li>
                                                <li className={`requirement_item ${/[^A-Za-z0-9]/.test(formData.newPassword) ? 'valid' : formData.newPassword ? 'invalid' : ''}`}>
                                                    One special char
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="password_field_group">
                                        <label className="password_label">Confirm New Password</label>
                                        <div className="password_input_wrapper">
                                            <div className="password_icon_left">
                                                <Lock size={16} />
                                            </div>
                                            <input
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`password_input ${errors.confirmPassword ? 'error' : ''}`}
                                                placeholder="Confirm your new password"
                                                disabled={isSubmitting}
                                            />
                                            <div
                                                className="password_icon_right"
                                                onClick={() => togglePasswordVisibility('confirm')}
                                            >
                                                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </div>
                                        </div>
                                        {errors.confirmPassword && (
                                            <div className="password_error">{errors.confirmPassword}</div>
                                        )}
                                        {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                                            <div className="password_match">
                                                <Check size={14} />
                                                <span>Passwords match</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="password_actions">
                                        <button
                                            type="submit"
                                            className={`password_submit_btn ${isSubmitting ? 'loading' : ''}`}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="spinner"></div>
                                                    Updating Password...
                                                </>
                                            ) : (
                                                <>
                                                    Update Password
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Security Tips */}
                            <div className="security_tips">
                                <h3>Security Tips</h3>
                                <ul>
                                    <li>Never share your password with anyone</li>
                                    <li>Use a unique password for this account</li>
                                    <li>Change your password regularly</li>
                                    <li>Avoid using personal information in your password</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChangePassword;
